import styled, { css } from 'styled-components';

export const FormSection = styled.div`
  margin-bottom: var(--spacing-8);
  width: 100%;
  max-width: 100%;

  @media (max-width: 768px) {
    margin-bottom: var(--spacing-6);
  }

  @media (max-width: 480px) {
    margin-bottom: var(--spacing-4);
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  margin-bottom: var(--spacing-6);

  @media (max-width: 768px) {
    gap: var(--spacing-1);
    margin-bottom: var(--spacing-4);
  }
`;

export const SectionTitle = styled.h3<{ $isGrouped?: boolean }>`
  font-size: ${({ $isGrouped }) =>
    $isGrouped === false ? 'var(--font-size-32)' : 'var(--font-size-20)'};
  font-weight: ${({ $isGrouped }) =>
    $isGrouped === false
      ? 'var(--font-weight-bold)'
      : 'var(--font-weight-semibold)'};
  color: var(--color-gray-900);
  margin: 0;
  line-height: var(--line-height-26);

  @media (max-width: 768px) {
    font-size: ${({ $isGrouped }) =>
      $isGrouped === false ? 'var(--font-size-24)' : 'var(--font-size-18)'};
    line-height: var(--line-height-24);
  }

  @media (max-width: 480px) {
    font-size: ${({ $isGrouped }) =>
      $isGrouped === false ? 'var(--font-size-20)' : 'var(--font-size-16)'};
    line-height: var(--line-height-21);
  }
`;

export const SectionDescription = styled.p`
  font-size: var(--font-size-16);
  color: var(--color-gray-600);
  margin: var(--spacing-1) 0 0 0;
  line-height: var(--line-height-21);

  @media (max-width: 768px) {
    font-size: var(--font-size-14);
    line-height: var(--line-height-18);
  }
`;

export const QuestionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
  &.single-column {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-3);
    margin-bottom: var(--spacing-4);
  }
`;

export const QuestionWrapper = styled.div<{
  $colSpan?: 1 | 2;
  $isCertificationStep?: boolean;
}>`
  grid-column: ${({ $colSpan }) => ($colSpan === 2 ? 'span 2' : 'span 1')};
  ${({ $isCertificationStep }) =>
    $isCertificationStep &&
    css`
      border: 1px solid var(--color-gray-300);
      border-radius: var(--radius-md);
      padding: var(--spacing-4);
      background: var(--color-white);

      @media (max-width: 768px) {
        padding: var(--spacing-3);
      }
    `}

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

export const QuestionLabel = styled.label<{ $mediumText?: boolean }>`
  display: block;
  font-size: ${({ $mediumText }) =>
    $mediumText ? 'var(--font-size-16)' : 'var(--font-size-18)'};
  font-weight: ${({ $mediumText }) =>
    $mediumText
      ? 'var(--font-weight-semibold)'
      : 'var(--font-weight-semibold)'};
  color: ${({ $mediumText }) =>
    $mediumText ? 'var(--color-gray-800)' : 'var(--color-gray-800)'};
  margin-bottom: var(--spacing-2);

  &.required::after {
    content: '*';
    color: var(--color-error);
  }

  @media (max-width: 768px) {
    font-size: ${({ $mediumText }) =>
      $mediumText ? 'var(--font-size-14)' : 'var(--font-size-16)'};
    margin-bottom: var(--spacing-1);
  }
`;

export const QuestionNote = styled.p`
  font-size: var(--font-size-16);
  color: var(--color-gray-500);
  margin: var(--spacing-1) 0 0 0;
  line-height: var(--line-height-18);
  font-style: italic;

  @media (max-width: 768px) {
    font-size: var(--font-size-14);
  }
`;

export const QuestionHelpText = styled.p`
  font-size: var(--font-size-16);
  color: var(--color-gray-400);
  margin: 0 0 var(--spacing-2) 0;
  line-height: var(--line-height-18);

  @media (max-width: 768px) {
    font-size: var(--font-size-14);
    margin: 0 0 var(--spacing-1) 0;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: var(--spacing-3);
  max-width: 100%; /* Add this */
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-16);
  font-family: var(--font-family-sans);
  line-height: var(--line-height-18);
  color: var(--color-gray-900);
  background-color: var(--color-white);
  transition: var(--transition-fast);
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box; /* Add this */

  &:focus {
    outline: none;
    border-color: var(--color-primary-500);
    box-shadow: 0 0 0 3px var(--color-primary-100);
  }

  &::placeholder {
    color: var(--color-gray-400);
  }

  &:disabled {
    background-color: var(--color-gray-50);
    color: var(--color-gray-500);
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: var(--spacing-2);
    font-size: var(--font-size-14);
    min-height: 60px;
  }
`;

export const Select = styled.select`
  width: 100%;
  max-width: 100%; /* Add this */
  padding: var(--spacing-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-16);
  font-family: var(--font-family-sans);
  line-height: var(--line-height-18);
  color: var(--color-gray-900);
  background-color: var(--color-white);
  transition: var(--transition-fast);
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--color-primary-500);
    box-shadow: 0 0 0 3px var(--color-primary-100);
  }

  &:disabled {
    background-color: var(--color-gray-50);
    color: var(--color-gray-500);
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: var(--spacing-2);
    font-size: var(--font-size-14);
  }
`;

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
`;

export const RadioOption = styled.label<{ $variant?: 'default' | 'black' }>`
  display: flex;
  align-items: center;
  width: fit-content;
  gap: var(--spacing-2);
  font-size: var(--font-size-16);
  color: var(--color-gray-700);
  cursor: pointer;

  &:hover {
    color: var(--color-gray-900);
  }

  @media (max-width: 768px) {
    font-size: var(--font-size-14);
    gap: var(--spacing-1);
  }
`;

export const RadioInput = styled.input<{ $variant?: 'default' | 'black' }>`
  appearance: none;
  width: 20px;
  height: 20px;
  border: 1px solid var(--color-gray-300);
  background-color: var(--color-gray-25);
  border-radius: 50%;
  position: relative;
  cursor: pointer;
  margin: 0;

  &:checked {
    border: 2px solid var(--color-primary-600);
    background-color: transparent;
  }

  &:checked::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--color-primary-600);
  }

  &:hover {
    border-color: var(--color-primary-600);
  }

  // &:disabled {
  //   background-color: var(--color-gray-50);
  //   border-color: var(--color-gray-200);
  //   cursor: not-allowed;
  // }

  &:disabled:checked {
    background-color: transparent;
    border: 2px solid var(--color-gray-300);
  }

  &:disabled:checked::after {
    background-color: var(--color-gray-300);
  }

  ${({ $variant }) =>
    $variant === 'black' &&
    `
    width: 18px;
    height: 18px;
    border: 2px solid var(--color-gray-300);
    background-color: transparent;

    &:checked {
      border: 2px solid var(--color-black);
      background-color: var(--color-black);
    }

    &:checked::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: var(--color-white);
    }

    &:hover {
      border-color: var(--color-gray-600);
    }

    &:hover:checked {
      border-color: var(--color-black);
    }

    &:disabled {
      background-color: var(--color-gray-100);
      border-color: var(--color-gray-200);
      cursor: not-allowed;
    }

    &:disabled:checked {
      background-color: var(--color-gray-100);
      border-color: var(--color-gray-400);
    }

    &:disabled:checked::after {
      background-color: var(--color-gray-400);
    }
  `}
`;

export const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
`;

export const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  font-size: var(--font-size-16);
  color: var(--color-gray-700);
  cursor: pointer;

  &:hover {
    color: var(--color-gray-900);
  }

  @media (max-width: 768px) {
    font-size: var(--font-size-14);
    gap: var(--spacing-1);
  }
`;

export const CheckboxInput = styled.input`
  margin: 0;
  cursor: pointer;
`;

export const FileUploadArea = styled.div`
  border: 2px dashed var(--color-gray-300);
  border-radius: var(--radius-lg);
  padding: var(--spacing-6);
  text-align: center;
  background-color: var(--color-gray-25);
  transition: var(--transition-fast);
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary-400);
    background-color: var(--color-primary-25);
  }

  &.dragover {
    border-color: var(--color-primary-500);
    background-color: var(--color-primary-50);
  }

  @media (max-width: 768px) {
    padding: var(--spacing-4);
  }
`;

export const FileUploadIcon = styled.div`
  font-size: var(--font-size-32);
  margin-bottom: var(--spacing-2);
  color: var(--color-gray-500);
`;

export const FileUploadText = styled.p`
  font-size: var(--font-size-16);
  color: var(--color-gray-600);
  margin: 0;
  line-height: var(--line-height-18);

  @media (max-width: 768px) {
    font-size: var(--font-size-14);
  }
`;

export const FileUploadSubtext = styled.p`
  font-size: var(--font-size-16);
  color: var(--color-gray-500);
  margin: var(--spacing-1) 0 0 0;
  line-height: var(--line-height-18);

  @media (max-width: 768px) {
    font-size: var(--font-size-14);
  }
`;

export const RowNote = styled.div`
  grid-column: span 2;
  padding: var(--spacing-3);
  background-color: var(--color-primary-25);
  border: 1px solid var(--color-primary-200);
  border-radius: var(--radius-md);
  margin-top: var(--spacing-2);

  @media (max-width: 768px) {
    grid-column: span 1;
    padding: var(--spacing-2);
  }
`;

export const RowNoteText = styled.p`
  font-size: var(--font-size-16);
  color: var(--color-primary-700);
  margin: 0;
  line-height: var(--line-height-18);
  font-style: italic;

  @media (max-width: 768px) {
    font-size: var(--font-size-14);
  }
`;

// Hotel Location Layout Components
export const HotelLocationContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-6);
  margin-bottom: var(--spacing-6);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-4);
    margin-bottom: var(--spacing-4);
  }
`;

export const LocationFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);

  @media (max-width: 768px) {
    gap: var(--spacing-3);
  }
`;

export const LocationFieldWrapper = styled.div`
  /* Individual field wrapper - no specific styles needed */
`;

export const MapContainer = styled.div`
  height: 100%;
  min-height: 100%;
`;

// Group Field Components
export const GroupFieldContainer = styled.div``;

export const GroupFieldHeader = styled.div`
  margin-bottom: var(--spacing-4);
  background-color: red;
`;

export const GroupFieldTitle = styled.h4`
  font-size: var(--font-size-16);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-900);
  margin: 0;
  margin-bottom: var(--spacing-2);
`;
